import { amenityCrudConfig, type Amenity } from '@/config/objects/amenities';
import { CrudListView, type CrudConfig } from '@startupquickstart/frontend';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface AmenitiesListProps {
  /**
   * Optional custom config (with modified endpoints, actions, etc.)
   */
  config?: CrudConfig<Amenity>;
  /**
   * Optional default where clause for filtering
   */
  defaultWhere?: Record<string, unknown>;
  /**
   * Optional custom onItemClick handler
   */
  onItemClick?: (amenity: Amenity) => void;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Base URL for navigation, defaults to /amenities
   */
  baseUrl?: string;
  /**
   * Breadcrumbs for navigation
   */
  breadcrumbs?: Array<{ label: string; href?: string; current?: boolean }>;
}

/**
 * Reusable amenities list component
 * Pass a custom config with modified actions to customize behavior
 */
export function AmenitiesList({
  config,
  baseUrl = '/amenities',
  defaultWhere,
  onItemClick,
  className,
  breadcrumbs,
}: AmenitiesListProps) {
  const navigate = useNavigate();

  const _onItemClick = useCallback(
    (amenity: Amenity) => {
      if (onItemClick) {
        onItemClick(amenity);
      } else {
        navigate(`/amenities/${amenity.id}`);
      }
    },
    [navigate, onItemClick],
  );

  return (
    <div className={className}>
      <CrudListView
        config={config || amenityCrudConfig}
        baseUrl={baseUrl}
        onItemClick={_onItemClick}
        defaultWhere={defaultWhere}
        breadcrumbs={breadcrumbs}
        getValueFromItem={(amenity: Amenity) => amenity.id}
        getLabelFromItem={(amenity: Amenity) => amenity.name}
      />
    </div>
  );
}
